import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "./calm";

/** The six areas the data is looked at in. They are nouns, all there at once: the scene claims no activity and no progress. */
export const STAGES = ["files", "columns", "assets", "duties", "risk", "report"] as const;
type Stage = (typeof STAGES)[number];

const L = ({ d, on, delay = 0 }: { d: string; on: boolean; delay?: number }) => (
  <path d={d} pathLength={1} className="onb-line" data-on={on ? "true" : "false"} style={{ transitionDelay: `${delay}ms` }} />
);

/** One area's icon, 64 x 48. */
function Icon({ stage, on }: { stage: Stage; on: boolean }) {
  return (
    <>
      {stage === "files" && (<><L on={on} d="M16 6h22l8 8v28H16z" /><L on={on} d="M38 6v8h8" delay={100} /><L on={on} d="M22 22h18M22 28h18M22 34h12" delay={200} /></>)}
      {stage === "columns" && (<><L on={on} d="M10 8h44v32H10z" /><L on={on} d="M10 19h44M10 30h44M25 8v32M40 8v32" delay={120} /><L on={on} d="M26 20h13v9H26z" delay={300} /></>)}
      {stage === "assets" && (<><L on={on} d="M12 14h28l12 10-12 10H12z" /><L on={on} d="M20 24h.01" delay={200} /><L on={on} d="M44 40h8M44 8h8" delay={260} /></>)}
      {stage === "duties" && (<><L on={on} d="M32 6l16 6v12c0 9-7 15-16 18-9-3-16-9-16-18V12z" /><L on={on} d="M25 24l5 5 9-10" delay={260} /></>)}
      {stage === "risk" && (<><L on={on} d="M8 42h48" /><L on={on} d="M16 42V22h12v20" delay={120} /><L on={on} d="M36 42V14h12v28" delay={260} /></>)}
      {stage === "report" && (<><L on={on} d="M14 6h30l6 6v32H14z" /><L on={on} d="M20 16h18M20 22h24M20 28h24" delay={160} /><L on={on} d="M40 34l3 3 6-6" delay={340} /></>)}
    </>
  );
}

/**
 * The six areas the data is looked at in, as one calm row - every one visible from the start, none ahead
 * of another. A single soft light wanders evenly across all six and back; it is decoration, not progress.
 * Nothing here is live: no numbers, no percentages, no found items, no file names. It stands still under
 * reduced motion and while the tab is hidden, and it says out loud (visibly) that it is an illustration.
 */
export default function AnalysisScene({ className }: { className?: string }) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(() => (typeof document === "undefined" ? true : !document.hidden));
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const listener = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  }, []);
  // the lines draw themselves once, all six together
  useEffect(() => {
    const frame = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const moving = !reduced && visible;

  return (
    <figure className={cn("w-full", className)} data-testid="analysis-scene" data-moving={moving ? "true" : "false"}>
      <div className="relative">
        {/* the track and the light: only where the six stand in one row */}
        <div className="pointer-events-none absolute inset-x-[8%] top-8 hidden h-px bg-border sm:block" aria-hidden="true" />
        {!reduced && (
          <div className="pointer-events-none absolute inset-x-[8%] top-8 hidden sm:block" aria-hidden="true">
            <span className="onb-wander absolute -top-[3px] left-0 h-[7px] w-[7px] rounded-full bg-primary/70" style={{ animationPlayState: moving ? "running" : "paused" }} data-testid="analysis-light" />
          </div>
        )}
        <ol className="relative grid grid-cols-3 gap-x-2 gap-y-6 sm:grid-cols-6" data-testid="analysis-stages">
          {STAGES.map((name) => (
            <li key={name} className="flex min-w-0 flex-col items-center gap-2 text-center" data-station={name}>
              <svg viewBox="0 0 64 64" className="h-16 w-16 text-foreground" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="32" cy="32" r="29" strokeWidth="1" className="fill-card text-border" />
                <g transform="translate(0 8)">
                  <Icon stage={name} on={drawn || reduced} />
                </g>
              </svg>
              <span className="text-xs font-medium leading-tight [overflow-wrap:anywhere] sm:text-sm">{t(`onboardingPage.analysis.stages.${name}`)}</span>
            </li>
          ))}
        </ol>
      </div>
      <figcaption className="mt-5 text-center text-xs text-muted-foreground" data-testid="analysis-caption">
        {t("onboardingPage.analysis.label")}
      </figcaption>
    </figure>
  );
}
